import React from 'react';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import UprightBassChordChanges from '../UprightBassChordChanges';
import {
  Tray, ControlCard, TrayLabel, KeyRows, StringsCard, StandardQuickButtons,
} from '../shared';

// ── Data ──────────────────────────────────────────────────────────────────────
// Only display labels live here. The option VALUES are the state values App.js and the
// SEO deep links use ('Full Scale', 'Pentatonic', 'Root & 5th', 'Triad', 'Chromatic').

/** Five structures: one 5-wide tray with short labels on the phone (open question 5). */
const STRUCTURE_OPTIONS_PHONE = [
  { value: 'Full Scale',  label: 'Scale' },
  { value: 'Pentatonic',  label: 'Penta' },
  { value: 'Root & 5th',  label: 'R & 5' },
  { value: 'Triad',       label: 'Triad' },
  { value: 'Chromatic',   label: 'Chrom' },
];
const STRUCTURE_OPTIONS_DESKTOP = [
  // KeyRows lays 5 options out 3 + 2 with a wider second row, so the long labels go last.
  { value: 'Full Scale',  label: 'Scale' },
  { value: 'Triad',       label: 'Triad' },
  { value: 'Chromatic',   label: 'Chromatic' },
  { value: 'Pentatonic',  label: 'Pentatonic' },
  { value: 'Root & 5th',  label: 'Root & 5th' },
];

/** Strings low to high, matching uprightBassStringFocus (E A D G). */
const STRINGS = [
  { label: 'E', ariaLabel: 'E string' },
  { label: 'A', ariaLabel: 'A string' },
  { label: 'D', ariaLabel: 'D string' },
  { label: 'G', ariaLabel: 'G string' },
];

const CHORD_CHANGE_OPTIONS = ['Off', 'On'];

// ── Controls: Key card + Strings card + Chord changes card ────────────────────

/**
 * Upright bass controls (boards 18/20 pattern): the Key card, the Strings card, and, while
 * Root & 5th is the structure, a Chord changes card (Off / On, then the key's seven chords).
 * The quick buttons live in {@link UprightBassQuickButtons}.
 */
export default function UprightBassControls({
  uprightBassKey,        onUprightBassKeyChange,
  uprightBassTonality,   onUprightBassTonalityChange,
  uprightBassStructure,  onUprightBassStructureChange,
  uprightBassStringFocus, onUprightBassStringToggle,
  uprightBassChordChangesActive, onUprightBassChordChangesChange,
  uprightBassSelectedDegrees,    onUprightBassDegreeToggle,
}) {
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'md' : 'sm';
  const isRootFifth = uprightBassStructure === 'Root & 5th';

  return (
    <>
      <ControlCard title="Key" size={size}>
        <KeyRows
          isDesktop={isDesktop}
          keyValue={uprightBassKey}
          onKeyChange={onUprightBassKeyChange}
          tonality={uprightBassTonality}
          onTonalityChange={onUprightBassTonalityChange}
          structureOptions={STRUCTURE_OPTIONS_PHONE}
          structureOptionsDesktop={STRUCTURE_OPTIONS_DESKTOP}
          structure={uprightBassStructure}
          onStructureChange={onUprightBassStructureChange}
        />
      </ControlCard>

      <StringsCard
        strings={STRINGS}
        focus={uprightBassStringFocus}
        onToggle={onUprightBassStringToggle}
        size={size}
      />

      {isRootFifth && (
        <ControlCard title="Chord changes" size={size}>
          <Tray
            ariaLabel="Chord changes"
            options={CHORD_CHANGE_OPTIONS}
            value={uprightBassChordChangesActive ? 'On' : 'Off'}
            onChange={(v) => onUprightBassChordChangesChange(v === 'On')}
            columns={2}
          />
          {uprightBassChordChangesActive && (
            <>
              {isDesktop && <TrayLabel className="mt-1">Chords</TrayLabel>}
              <UprightBassChordChanges
                musicalKey={uprightBassKey}
                selectedDegrees={uprightBassSelectedDegrees}
                onDegreeToggle={onUprightBassDegreeToggle}
              />
            </>
          )}
        </ControlCard>
      )}
    </>
  );
}

// ── Quick buttons: Filter / Labels / Reset ────────────────────────────────────

/**
 * The three quick buttons for the layout's `quickButtons` slot.
 * Filter cycles All Notes -> Triads Only -> Roots Only and is greyed while Chord changes is on
 * (those dots ignore the filter). Labels toggles Note Names <-> Intervals ('None' counts as Names).
 */
export function UprightBassQuickButtons({
  uprightBassChordChangesActive,
  uprightBassFilter, onUprightBassFilterChange,
  uprightBassLabel,  onUprightBassLabelChange,
  onUprightBassReset,
}) {
  const isDesktop = useIsDesktop();
  return (
    <StandardQuickButtons
      size={isDesktop ? 'md' : 'sm'}
      filter={uprightBassFilter}
      onFilterChange={onUprightBassFilterChange}
      filterDisabled={uprightBassChordChangesActive}
      label={uprightBassLabel}
      onLabelChange={onUprightBassLabelChange}
      onReset={onUprightBassReset}
    />
  );
}
